/**
 * The legacy permission model, ported deliberately rather than reinvented.
 *
 * Identity is a concatenated string matched exactly:
 *   "{userType} - {role}"  or  "{userType} - TenantAdmin - {role}"
 *
 * 26 exported symbols in the legacy `route_access.js` resolve to 46 distinct
 * strings (see docs/inventory/route-access.json). Two known hazards, recorded in
 * docs/inventory/FINDINGS.md, are handled explicitly here rather than inherited:
 *
 *   P3  the empty string '' is a valid role that reaches 10 routes
 *   P4  "No Access" is a grantable role that reaches 13-16 routes
 *
 * Both are treated as no-access here. If the backend disagrees, that is a
 * backend finding to fix, not a behavior to reproduce.
 *
 * IMPORTANT: everything in this file is a UX affordance. It decides what to show,
 * never what is allowed. Authorization is the server's job.
 */

export type UserType = 'Root' | 'OrgAdmin' | 'User' | 'ContentManager'
export type SecurityRole = 'Security Engineer' | 'Security Analyst' | 'Viewer' | 'No Access'
export type ProductTier = 'essential' | 'professional' | 'enterprise'

export interface Identity {
  userType: UserType
  role: SecurityRole
  isTenantAdmin: boolean
  tier: ProductTier
  displayName: string
  email: string
  /**
   * Per-product entitlement (docs/IA.md IA14). null = not licensed; the product
   * group renders locked with a capability page instead of disappearing.
   */
  products: { fabric: ProductTier | null; vantage: ProductTier | null; synthai: ProductTier | null }
}

/** The exact string the legacy backend sends, rebuilt from its parts. */
export function roleString(id: Identity): string {
  return id.isTenantAdmin
    ? `${id.userType} - TenantAdmin - ${id.role}`
    : `${id.userType} - ${id.role}`
}

/** Roles a nav entry or route can require. */
export type RoleRequirement = SecurityRole[] | 'any'

export function hasRole(id: Identity, required: RoleRequirement): boolean {
  // P4: "No Access" reaches routes in the legacy model. It does not here.
  if (id.role === 'No Access') return false
  if (required === 'any') return true
  return required.includes(id.role)
}

export function hasTier(id: Identity, tiers?: ProductTier[]): boolean {
  if (!tiers || tiers.length === 0) return true
  return tiers.includes(id.tier)
}

export function canSee(
  id: Identity,
  opts: {
    roles?: RoleRequirement
    tiers?: ProductTier[]
    tenantAdminOnly?: boolean
    /**
     * An arbitrary predicate, for gates the role/tier pair cannot express.
     *
     * The Settings sections need this: their legacy gates are ORs ACROSS
     * dimensions -- "TenantAdmin whatever your role, OR a Security Engineer" --
     * and every field above ANDs. Folding those into roles/tiers would either
     * over-grant (drop the user-type half) or under-grant (drop the role half).
     * See SETTINGS_GATES below.
     */
    gate?: (id: Identity) => boolean
    /**
     * With a `gate` only: let the gate alone decide for a 'No Access'
     * identity, which P4 otherwise refuses everywhere. Opted into per entry
     * — Vantage › Cases admits every Root user, 'Root - No Access' included.
     */
    admitsNoAccess?: boolean
  },
): boolean {
  if (opts.tenantAdminOnly && !id.isTenantAdmin) return false
  if (opts.gate && !opts.gate(id)) return false
  if (opts.gate && opts.admitsNoAccess && id.role === 'No Access') return hasTier(id, opts.tiers)
  return hasRole(id, opts.roles ?? 'any') && hasTier(id, opts.tiers)
}

/* ------------------------------------------------------- settings-area gates */

/**
 * Who reaches each Settings section, ported from the legacy nav's role lists
 * (docs/inventory/route-access.json; docs/settings-requirements.md S4).
 *
 * These are USER-TYPE gates as much as role gates, which is why they live here
 * as predicates rather than as `roles:` arrays. Read them as the legacy nav
 * read: a TenantAdmin reaches the operational sections at any role, and a
 * user-type grant (Root, OrgAdmin) reaches the tenancy sections independently
 * of role.
 *
 * P3/P4 still hold: `hasRole` already refuses 'No Access', so a legacy
 * "TenantAdmin - No Access" -- which reached these routes -- reaches nothing
 * here. That is deliberate, and it is a legacy finding, not a regression.
 */
const isEngineer = (id: Identity) => id.role === 'Security Engineer'

export const SETTINGS_GATES = {
  /** Operational config: tenant admins, or an engineer in this scope. */
  operational: (id: Identity) => id.isTenantAdmin || isEngineer(id),
  /** Tenancy: the cluster's own admins. */
  tenants: (id: Identity) => id.isTenantAdmin || id.userType === 'Root',
  /** Org membership: whoever administers an org. */
  orgs: (id: Identity) => id.userType === 'Root' || id.userType === 'OrgAdmin',
  /**
   * People. Tenant admins and Root only — NOT OrgAdmin.
   *
   * Settings → Users is CROSS-ORGANIZATION: it calls `/lc/mgmt/org/user-list`
   * WITHOUT an org id, so it returns every organization's users, with disable
   * and credential resets on each row. An OrgAdmin reaching it could reset
   * passwords for people in organizations they do not administer.
   *
   * The legacy console never had this screen live — its flat route is
   * commented out, and the surviving org-NESTED one was gated to Root. So
   * there is no legacy grant to inherit here, and the narrow reading is the
   * one that matches what the screen can actually do.
   *
   * The per-org users view this anticipated now exists, on the Organizations
   * detail page (§6.5). It takes the OrgAdmin grant as predicted — and it
   * takes it by showing LESS: the same endpoint scoped by `orgId`, read-only,
   * with every credential action left on this screen.
   */
  users: (id: Identity) => id.isTenantAdmin || id.userType === 'Root',
  /** Deployment-wide mail config. Root only, as in the legacy nav. */
  root: (id: Identity) => id.userType === 'Root',
} satisfies Record<string, (id: Identity) => boolean>
